import React from "react";
import type { IOccurrencesAssociatedPeople, ResponseProps } from "../interfaces";
import { createState, useState, State } from "@hookstate/core";
import { getOccurrencesAssociatedPeopleArrested, getOccurrencesAssociatedPeopleByAge, getOccurrencesAssociatedPeopleByCircunstance, getOccurrencesAssociatedPeopleByDestination, getOccurrencesAssociatedPeopleByGender, getOccurrencesAssociatedPeopleByInvolvementType, getOccurrencesAssociatedPeopleByOccupation, getOccurrencesAssociatedPeopleByOccurrenceType, getOccurrencesAssociatedPeopleByOrganization, getOccurrencesAssociatedPeopleByPublicAgent } from "../components/QualifiersCharts/InvolvedChart/services";
interface IOccurrencesAssociatedPeopleProps {
  readonly occurrencesAssociatedPeople: IOccurrencesAssociatedPeople;
  readonly loadingCharts: boolean;
};

const occurrencesAssociatedPeopleProps: Readonly<ResponseProps> = {
  item: null,
  value: null,
  total: null
};

const occurrencesAssociatedPeopleDefault: Readonly<IOccurrencesAssociatedPeople> = {
  occurrencesAssociatedPeopleByInvolvementType: occurrencesAssociatedPeopleProps,
  occurrencesAssociatedPeopleArrested: occurrencesAssociatedPeopleProps,
  occurrencesAssociatedPeopleByCircunstance: occurrencesAssociatedPeopleProps,
  occurrencesAssociatedPeopleByDestination: occurrencesAssociatedPeopleProps,
  occurrencesAssociatedPeopleByPublicAgent: occurrencesAssociatedPeopleProps,
  occurrencesAssociatedPeopleByOrganization: occurrencesAssociatedPeopleProps,
  occurrencesAssociatedPeopleByGender: occurrencesAssociatedPeopleProps,
  occurrencesAssociatedPeopleByOccupation: occurrencesAssociatedPeopleProps,
  occurrencesAssociatedPeopleByAge: occurrencesAssociatedPeopleProps,
  occurrencesAssociatedPeopleByOccurrenceType: occurrencesAssociatedPeopleProps
};

const store = createState<Readonly<IOccurrencesAssociatedPeopleProps>>({
  occurrencesAssociatedPeople: occurrencesAssociatedPeopleDefault,
  loadingCharts: false,
});

interface getAllsAssociatedPeopleDataProps {
  readonly startDate: string | null;
  readonly finishDate: string | null;
  readonly involvementTypeId: number | null;
  readonly arrested: number | null;
  readonly peopleCircunstanceId: number | null;
  readonly destinationId: number | null;
}

export default function useManagementQualifiersInvolvedState() {
  const occurrencesAssociatedPeople: State<Readonly<IOccurrencesAssociatedPeople>> = useState(store.occurrencesAssociatedPeople);
  const loadingCharts = useState(store.loadingCharts)


  async function getAssociatedPeopleStats({ startDate, finishDate, involvementTypeId, arrested, peopleCircunstanceId, destinationId }: getAllsAssociatedPeopleDataProps) {
    const params = { startDate, finishDate, arrested, involvementTypeId, peopleCircunstanceId, destinationId };

    const mapResponse = (response) =>
      response.map(({ item, value, total }) => ({ label: item, value, total }))

    loadingCharts.set(true)
    Promise.all([
      getOccurrencesAssociatedPeopleByInvolvementType(params).then((result) => {
        occurrencesAssociatedPeople.occurrencesAssociatedPeopleByInvolvementType.set(result)
      }),

      getOccurrencesAssociatedPeopleArrested(params).then((response) => {
        occurrencesAssociatedPeople.occurrencesAssociatedPeopleArrested.set(mapResponse(response))
      }),

      getOccurrencesAssociatedPeopleByCircunstance(params).then((result) => {
        occurrencesAssociatedPeople.occurrencesAssociatedPeopleByCircunstance.set(result)
      }),

      getOccurrencesAssociatedPeopleByDestination(params).then((result) => {
        occurrencesAssociatedPeople.occurrencesAssociatedPeopleByDestination.set(result)
      }),

      getOccurrencesAssociatedPeopleByPublicAgent(params).then((response) => {
        occurrencesAssociatedPeople.occurrencesAssociatedPeopleByPublicAgent.set(mapResponse(response))
      }),

      getOccurrencesAssociatedPeopleByOrganization(params).then((result) => {
        occurrencesAssociatedPeople.occurrencesAssociatedPeopleByOrganization.set(result)
      }),

      getOccurrencesAssociatedPeopleByGender(params).then((response) => {
        occurrencesAssociatedPeople.occurrencesAssociatedPeopleByGender.set(mapResponse(response))
      }),

      getOccurrencesAssociatedPeopleByOccupation(params).then((result) => {
        occurrencesAssociatedPeople.occurrencesAssociatedPeopleByOccupation.set(result)
      }),

      getOccurrencesAssociatedPeopleByAge(params).then((result) => {
        occurrencesAssociatedPeople.occurrencesAssociatedPeopleByAge.set(result)
      }),
      getOccurrencesAssociatedPeopleByOccurrenceType(params).then((result) => {
        occurrencesAssociatedPeople.occurrencesAssociatedPeopleByOccurrenceType.set(result)
      })
    ]).finally(() => {
      loadingCharts.set(false)
    })
  }

  return {
    occurrencesAssociatedPeople: () => occurrencesAssociatedPeople,
    fetchAllAssociatedPeopleData: async ({
      arrested,
      destinationId,
      finishDate,
      involvementTypeId,
      peopleCircunstanceId,
      startDate }) =>
      getAssociatedPeopleStats({
        arrested: arrested,
        destinationId: destinationId,
        finishDate: finishDate,
        involvementTypeId: involvementTypeId,
        peopleCircunstanceId: peopleCircunstanceId,
        startDate: startDate
      }),
    loadingCharts: loadingCharts
  };
}
